import { runtimeConfig } from "@src/runtime-config";

/**
 * Adds a numbered environment to the URL.
 * E.g. https://sphere.holobuilder.com -> https://dev1.sphere.holobuilder.com
 *
 * @param url The original url
 * @param numberedEnv The numbered environment to add
 * @returns New url with the numbered environment added
 */
export function addNumberedEnv(
  url: string,
  numberedEnv?: string | null
): string {
  if (!numberedEnv) {
    return url;
  }

  const urlObj = new URL(url);

  // Add numbered environment only to sphere.*.holobuilder.com or sphere.*.eu URLs
  if (urlObj.hostname.match(/entry|sphere|admin.*\.holobuilder\.(com|eu)/)) {
    const parts = urlObj.hostname.split(".");
    parts[0] = `${numberedEnv}.${parts[0]}`;
    urlObj.hostname = parts.join(".");
  }

  return urlObj.toString();
}

/** Gets the URL for the init route of the Admin Panel API. */
export function getAdminPanelApiUrl(
  sphereEntryPageApi: string,
  numberedEnv: string | null
): string {
  return addNumberedEnv(sphereEntryPageApi, numberedEnv);
}

/**
 * Returns the pathname from the URL with the ids replaced by the key names.
 *
 * @param url A URL with ids in the path, e.g. /project/1234
 * @param appParams An object with the key names and their corresponding ids, e.g. { projectId: 1234 }
 * @returns The masked pathname, e.g. /project/:projectId
 */
export function getPathnameFromUrl(
  originalPathname: string,
  appParams: Partial<Record<string, string>>
): string {
  let pathname = originalPathname;
  let key: keyof typeof appParams;
  for (key in appParams) {
    // Replace all ids in the path with the key name
    // e.g. /project/1234 -> /project/:projectId
    if (key in appParams && key.endsWith("Id")) {
      pathname = pathname.replace(`${appParams[key]}`, `:${key}`);
    }
  }

  return pathname;
}

/**
 * Returns the query parameters from the search string.
 *
 * @param search The search string from the URL, e.g. ?key1=value1&key2=value2
 * @returns The query parameters, e.g. ["key1", "key2"]
 */
export function getQueryParamsFromSearch(search: string): string[] {
  if (!search || search === "?") {
    return [];
  }
  const searchSplit = search.split("?");
  const queryParams = searchSplit.length > 1 ? searchSplit[1] : searchSplit[0];
  return queryParams.split("&").map((param) => param.split("=")[0]);
}

/** @returns The query parameter value parsed as boolean, or the default value. */
export function getBooleanQueryParam(
  search: string,
  key: string,
  isDefaultTrue: boolean = false
): boolean {
  try {
    const searchParams = new URLSearchParams(search);
    const value = searchParams.get(key)?.toLowerCase() ?? "";
    return isDefaultTrue ? value !== "false" : value === "true";
  } catch {
    return isDefaultTrue;
  }
}

/**
 * Gets the URL to navigate to the entry page,
 * with the current URL set as a query parameter for redirect after login, and the server selection.
 * @param path Optional path to append to the entry page URL, e.g. "/logout"
 * @returns The full URL to navigate to the entry page
 */
export function getEntryPageUrl({ path = "" }: { path?: string }): string {
  const entryPageUrl = new URL(runtimeConfig.urls.entryPageUrl + path);
  /** Sets the URL to redirect back to the admin panel after a successful login */
  entryPageUrl.searchParams.set("redirectUrl", globalThis.location.href);
  /** Selects a server so that once user inputs a valid email, they don't have to select the server */
  const server =
    runtimeConfig.appRegion === "com" ? "sphere_2_com" : "sphere_2_eu";
  entryPageUrl.searchParams.set("server", server);
  return entryPageUrl.toString();
}

/** Which page of Sphere a link opens */
export type SphereLinkTarget = "workspace-list" | "workspace" | "project";

/** Link to a page of Sphere in the current environment */
export type SphereLink = {
  /** The full URL of the Sphere page */
  url: string;

  /** Which page of Sphere the URL opens */
  target: SphereLinkTarget;
};

/**
 * Gets the link to the most specific Sphere page for the given IDs:
 * the project, the workspace's project list, or the workspace list.
 * A project can only be linked together with its workspace.
 *
 * @param props.companyId ID of the workspace to open
 * @param props.projectId ID of the project to open, inside the workspace
 * @returns The Sphere link and which page it opens
 */
export function getSphereLink({
  companyId,
  projectId,
}: {
  companyId?: string;
  projectId?: string;
}): SphereLink {
  const dashboardUrl = runtimeConfig.urls.dashboardUrl;
  if (!companyId) {
    return { url: dashboardUrl, target: "workspace-list" };
  }

  const workspaceUrl = `${dashboardUrl}/${encodeURIComponent(companyId)}/projects`;
  if (!projectId) {
    return { url: workspaceUrl, target: "workspace" };
  }

  return {
    url: `${workspaceUrl}/${encodeURIComponent(projectId)}`,
    target: "project",
  };
}
